/**
 * Field adapter over the unified `useScopedSettingsCore`. Given a tool's
 * declarative `scope` ({ levels, perMode }) and `fields`, it exposes the flat
 * `{ value, setField }` surface the FieldList renders, while delegating ALL
 * scope logic (which slot, inherit/Customize/Remove, Copy from User, Cancel,
 * Save, the dropdown) to the shared core. Re-scoping a tool is just changing the
 * `scope.levels` it's passed (e.g. "tab" → "workspace-user").
 *
 * The edited value is the shared `ChartTheme` (drawing-tool colours/widths +
 * Chart Properties live in the same theme object); `value` is just the `fields`
 * subset. `perMode` stores separate light/dark slots — handled by routing the
 * active mode's slot in/out of the core and snapshotting BOTH modes for Cancel.
 */
import { useMemo } from "react";
import {
  useAppDispatch,
  useAppSelector,
  useActiveTabId,
} from "@/state/hooks";
import { store } from "@/state/store";
import { setUserTheme, saveUserTheme } from "@/state/slices/userPrefsSlice";
import { setTabTheme, type ChartTheme } from "@/state/slices/uiSlice";
import { selectEffectiveTheme } from "@/state/selectors/effectiveTheme";
import type { MoreAction } from "@/components/MoreActionsMenu/MoreActionsMenu";
import type { Field, ScopeConfig } from "./registry";
import type { ScopeChrome } from "./SettingsPanel";
import { useScopedSettingsCore } from "./useScopedSettingsCore";

export interface ScopedSetting {
  value: Record<string, unknown>;
  scopeUi?: ScopeChrome;
  actions: MoreAction[];
  setField: (key: string, value: unknown) => void;
  cancel: () => void;
  save: () => void;
  /** True at workspace scope with no tab override — render the Customize CTA
   *  instead of the field editor (only ever true for "workspace-user" tools). */
  isInheriting: boolean;
  /** Seed a workspace override from the inherited value. */
  customize: () => void;
}

// Snapshot of all four theme slots, so Cancel restores both light + dark across
// both scopes regardless of which the user touched.
interface ThemeSnap {
  tabDark: ChartTheme | null;
  tabLight: ChartTheme | null;
  userDark: ChartTheme | null;
  userLight: ChartTheme | null;
}

export function useScopedSetting(
  scope: ScopeConfig,
  fields: Field[],
  open: boolean,
): ScopedSetting {
  const dispatch = useAppDispatch();
  const tabId = useActiveTabId();
  const appMode = useAppSelector((s) => s.appearance.theme); // "light" | "dark"
  const mode: "light" | "dark" = scope.perMode ? appMode : "dark";

  const effective = useAppSelector((s) => selectEffectiveTheme(s, tabId));
  const tabDark = useAppSelector((s) => s.ui.byTab[tabId]?.theme ?? null);
  const tabLight = useAppSelector((s) => s.ui.byTab[tabId]?.themeLight ?? null);
  const userDark = useAppSelector((s) => s.userPrefs.theme);
  const userLight = useAppSelector((s) => s.userPrefs.themeLight);

  // Active-mode slots fed to the core.
  const tabSlot = mode === "light" ? tabLight : tabDark;
  const userSlot = mode === "light" ? userLight : userDark;

  const core = useScopedSettingsCore<ChartTheme, ThemeSnap>(
    {
      levels: scope.levels,
      tabValue: tabSlot,
      userValue: userSlot,
      // The seed/read base is the fully-resolved theme (preset → user → tab),
      // so a fresh field reads the value the chart actually shows and a first
      // write materialises a full theme — matching the prior behaviour.
      defaultValue: effective,
      writeTab: (v) => dispatch(setTabTheme({ tabId, mode, theme: v })),
      writeUser: (v) => dispatch(setUserTheme({ mode, theme: v })),
      persistUser: () => {
        const s = store.getState();
        dispatch(
          saveUserTheme({
            theme: s.userPrefs.theme,
            themeCache: s.userPrefs.themeCache,
            themeLight: s.userPrefs.themeLight,
            themeCacheLight: s.userPrefs.themeCacheLight,
          }),
        );
      },
      clone: (t) => ({ ...t }),
      // Cancel restores all four slots (both modes, both scopes).
      captureSnapshot: () => ({
        tabDark: tabDark ? { ...tabDark } : null,
        tabLight: tabLight ? { ...tabLight } : null,
        userDark: userDark ? { ...userDark } : null,
        userLight: userLight ? { ...userLight } : null,
      }),
      restoreSnapshot: (snap) => {
        dispatch(setTabTheme({ tabId, mode: "dark", theme: snap.tabDark }));
        dispatch(setTabTheme({ tabId, mode: "light", theme: snap.tabLight }));
        dispatch(setUserTheme({ mode: "dark", theme: snap.userDark }));
        dispatch(setUserTheme({ mode: "light", theme: snap.userLight }));
      },
    },
    open,
  );

  // The flat field-subset of the resolved theme.
  const value = useMemo(() => {
    const cur = core.current as unknown as Record<string, unknown> | null;
    const v: Record<string, unknown> = {};
    for (const f of fields) {
      if (f.kind === "header") continue;
      v[f.key] = cur?.[f.key];
      if (f.kind === "color" && f.matchKey) v[f.matchKey] = cur?.[f.matchKey];
    }
    return v;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [core.current, fields]);

  const setField = (key: string, val: unknown) => {
    core.patch({ ...(core.current as ChartTheme), [key]: val });
  };

  // Field-scoped reset: clear ONLY this tool's fields back to their declared
  // defaults within the current theme (leave the rest of the theme intact).
  const resetFields = () => {
    const next = { ...core.current } as unknown as Record<string, unknown>;
    for (const f of fields) {
      if (f.kind === "header") continue;
      if ("default" in f && f.default !== undefined) next[f.key] = f.default;
      else delete next[f.key];
      if (f.kind === "color" && f.matchKey) delete next[f.matchKey];
    }
    core.patch(next as unknown as ChartTheme);
  };

  // Actions: field-scoped reset (always when editable) + the 2-scope actions
  // (Copy from User / Remove override) when the dropdown is shown and we have an
  // override. isInheriting hides everything (the body shows the Customize CTA).
  const actions: MoreAction[] = [];
  if (!core.isInheriting) {
    actions.push({
      label: "Reset to default",
      title: "Reset this tool's appearance to the built-in default",
      onClick: resetFields,
    });
  }
  if (core.scopeUi && core.scope === "workspace" && !core.isInheriting) {
    actions.push({
      label: "Copy from User defaults",
      title: "Replace this chart's appearance with your saved User defaults",
      onClick: core.copyFromUser,
    });
    actions.push({
      label: "Remove workspace override",
      title: "Revert this chart tab to User defaults",
      onClick: core.removeOverride,
    });
  }

  return {
    value,
    scopeUi: core.scopeUi,
    actions,
    setField,
    cancel: core.cancel,
    save: () => {
      void core.save();
    },
    isInheriting: core.isInheriting,
    customize: core.customize,
  };
}
